import React, { useCallback, useEffect, useRef, useState } from 'react';
import useStore from '../../../stores/helpers/use-context';
import { Map as MapboxMap, Popup } from 'mapbox-gl';
import TaskTooltipWrapper from '../../../components/task-tooltip-body/task-tooltip-wrapper';
import ReactDOM from 'react-dom';
import { StoreProvider } from '../../../stores/helpers/store-context';
import { NO_ROUTE_ID } from '../../../stores/routes-planner-store';
import {
	markersLayerId,
	ungroupedMarkersLayerId
} from '@bringg/react-components/dist/components/mapbox/utils/markers-manager';
import { sortTasksByRunIdAndPriority } from '../../../stores/helpers/helpers';
import { fitBoundsByMarkers } from '@bringg/react-components/dist/components/mapbox/mapbox-route-tracker/utils/mapbox-bounds-fit';
import type { RoutesPlannerTask } from '../../../types';

const taskIdPopup = new Popup({
	className: 'task-id-popup',
	closeButton: false,
	closeOnClick: false,
	offset: [0, -30]
});

const tasksPopup = new Popup({
	closeButton: false,
	closeOnClick: false,
	offset: [0, 0],
	className: 'task-tooltip-on-map'
});

const useMap = () => {
	const routesPlannerStore = useStore();
	const { mapMarkers } = routesPlannerStore.mapStore;
	const mapContainer = useRef<MapboxMap>();
	const tasksTooltipRef = useRef<HTMLDivElement>();
	const [isMapLoaded, setIsMapLoaded] = useState<boolean>();

	useEffect(() => {
		const map = mapContainer.current;
		if (map && isMapLoaded) {
			fitBoundsByMarkers(map, mapMarkers);
		}
	}, [routesPlannerStore.runsToDisplay, isMapLoaded]);

	const removeTasksPopup = () => {
		tasksPopup.remove();
		routesPlannerStore.setSelectedTaskId(null);
	};

	const showTaskIdPopup = (task: RoutesPlannerTask) => {
		const popupDescription = `<span>${task.external_id}</span>`;
		const { lat, lng } = task.way_points[0];

		taskIdPopup.setLngLat([lng, lat]).setHTML(popupDescription).addTo(mapContainer.current);
	};

	const showTasksPopup = (taskIds: number[]) => {
		const popupNode = document.createElement('div');

		ReactDOM.render(
			<StoreProvider value={routesPlannerStore}>
				<div ref={tasksTooltipRef}>
					<TaskTooltipWrapper
						taskIds={taskIds}
						removeTooltip={removeTasksPopup}
						containerRef={tasksTooltipRef}
					/>
				</div>
			</StoreProvider>,
			popupNode
		);

		const { lat, lng } = routesPlannerStore.tasksById.get(taskIds[0]).way_points[0];

		tasksPopup.setLngLat([lng, lat]).setDOMContent(popupNode).addTo(mapContainer.current);
	};

	const highlightPopup = (taskId: number) => {
		if (mapContainer.current) {
			const task = routesPlannerStore.tasksById.get(taskId);
			if (task) {
				const runId = task.run_id ?? NO_ROUTE_ID;
				if (routesPlannerStore.isRunDisplayed(runId)) {
					showTaskIdPopup(task);
				}
			}
		}
	};

	const removeTaskIdPopup = () => {
		taskIdPopup.remove();
	};

	const onMarkerFeaturesClick = (markerFeatures: GeoJSON.Feature[]) => {
		const tasksToDisplay: RoutesPlannerTask[] = [];

		markerFeatures.forEach(markerFeature => {
			const id = markerFeature.properties.id.split('_');
			const taskId = Number(id[2]);
			const task = routesPlannerStore.tasksById.get(taskId);
			tasksToDisplay.push(task);
		});

		const taskIds = sortTasksByRunIdAndPriority(tasksToDisplay).map(task => task.id);
		showTasksPopup(taskIds);
	};

	const onMapLoaded = (map: MapboxMap) => {
		mapContainer.current = map;
		map.resize();

		map.on('click', e => {
			const layers = [];
			if (map.getLayer(markersLayerId)) {
				layers.push(markersLayerId);
			}
			if (map.getLayer(ungroupedMarkersLayerId)) {
				layers.push(ungroupedMarkersLayerId);
			}

			const selectedFeatures = map.queryRenderedFeatures(e.point, { layers });
			if (selectedFeatures.length === 0) {
				return;
			}
			onMarkerFeaturesClick(selectedFeatures);
		});
		setIsMapLoaded(true);
	};

	const resizeMap = useCallback(() => {
		const map: MapboxMap = mapContainer.current;
		if (map) {
			map.resize();
		}
	}, [mapContainer.current]);

	return { onMapLoaded, highlightPopup, removeTaskIdPopup, resizeMap };
};

export default useMap;
